import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { EXTRA } from "./extra";

export const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "bn", label: "বাংলা", flag: "🇧🇩" },
  { code: "hi", label: "हिन्दी", flag: "🇮🇳" },
  { code: "ne", label: "नेपाली", flag: "🇳🇵" },
];

const en = {
  nav: { courses: "Courses", vocab: "Vocabulary", practice: "Practice", progress: "Progress", admin: "Admin", culture: "Culture", grammar: "Grammar", dict: "Pictures", login: "Log in", logout: "Log out" },
  common: { back: "Back", loading: "Loading…", next: "Next", prev: "Previous" },
  home: {
    badge: "Finnish for all levels · A1 · A2 · B1",
    title: "Learn Finnish, step by step",
    sub: "SuomiStep is a guided Finnish course for new students — from your very first words to everyday conversations. Enrol, get approved by your teacher, and start learning.",
    ctaEnrol: "Enrol / Log in",
    ctaLearnMore: "See what you'll learn",
    statLessons: "lessons",
    statWords: "vocabulary words",
    statCases: "grammar cases",
    coveredTitle: "What you'll learn in this course",
    coveredSub: "A complete beginner path, carefully ordered from easy to hard. Here is everything the course covers.",
    a1Title: "Level A1 · Beginner",
    a1Desc: "Start from zero: the alphabet and sounds, greetings, numbers, everyday grammar, and simple conversations like ordering at a café.",
    a2Title: "Level A2 · Elementary",
    a2Desc: "Build real fluency: past and perfect tenses, the key grammatical cases, comparisons, and practical topics like health, housing and future plans.",
    b1Title: "Level B1 · Advanced",
    b1Desc: "Go further: the conditional, the passive, total vs partial objects, verb cases, participles and real spoken Finnish — the grammar that makes you sound fluent.",
    howTitle: "How it works",
    how1Title: "1. Log in",
    how1Body: "Sign in securely with your Google account to create your student profile.",
    how2Title: "2. Get approved",
    how2Body: "Your teacher reviews your request and grants you access to the lessons.",
    how3Title: "3. Start learning",
    how3Body: "Once approved, unlock all lessons, flashcards, quizzes and progress tracking.",
    enrolTitle: "Ready to begin?",
    enrolBody: "Log in to request access. Your teacher will approve you and you can start studying right away.",
    footer: "An original Finnish course for new students.",
    comingBadge: "Coming to Finland? Don't miss this course.",
    finlandTitle: "Learn the language of everyday life in Finland",
    finlandBody: "From Helsinki's markets and trams to the café counter and the workplace — this course prepares you for real Finnish life, not just the textbook.",
    jobTitle: "After the course: your path to work in Finland",
    jobBody: "When you finish the lessons, we don't stop there. We guide you through job-finding techniques and preparation for Finland — how to write a Finnish-style CV, where to look for jobs, how to approach interviews, and what to expect from working life here.",
    jobTag: "Included after you complete the course",
  },
  gate: {
    loginTitle: "Please log in to continue",
    loginBody: "The lessons are for enrolled students. Log in with Google to request access.",
    loginBtn: "Log in with Google",
    pendingTitle: "Your access is pending approval",
    pendingBody: "Thanks for enrolling! Your teacher needs to approve your account before you can start. Please check back soon.",
    pendingHint: "You'll get full access to all lessons, flashcards and quizzes once approved.",
    backHome: "Back to home",
  },
  lesson: {
    back: "Back to courses", complete: "Mark as complete", completed: "Completed", takeQuiz: "Take the quiz",
    keywords: "Key words", testKnowledge: "Test your knowledge", listen: "Listen", loading: "Loading…",
    saved: "Great! Lesson marked complete. 🎉", saveFail: "Could not save.",
  },
  flash: {
    title: "Vocabulary", sub: "Flip the card and listen to the pronunciation.", all: "All", tap: "Tap to flip",
    translation: "translation", know: "I know it", learned: "Learned", review: "Review", mastered: "Learned",
    savedLearned: "marked as learned ✓", savedReview: "added to review", learnedCount: "Learned",
  },
  quiz: {
    hubTitle: "Lesson Quizzes", hubSub: "Test your knowledge after each lesson.", qOf: "Question", check: "Check",
    next: "Next", showResults: "Show results", correct: "Correct! Well done!", wrong: "Not quite. Correct answer:",
    writeAnswer: "Type your answer…", resultPerfect: "Perfect! Excellent work.", resultGood: "Good job! Keep practising.",
    resultBad: "Good try — review the lesson and try again.", again: "Try again", backLesson: "Back to lesson",
    saveLogin: "Log in to save your results.", loginBtn: "Log in",
  },
  dash: {
    hi: "Hi", welcome: "Welcome back to learning Finnish.", streak: "day streak", longest: "Longest",
    lessons: "lessons", accuracy: "quiz accuracy", quizzesDone: "quizzes taken", words: "words learned",
    levelProgress: "Level progress", resumeFrom: "Continue from here", allDone: "You've completed every lesson. Congratulations!",
    calendar: "Streak calendar", last5: "Last 5 weeks", noActivity: "no activity", practiced: "practised",
    loginTitle: "Log in to see your progress", loginBody: "Track your streak, scores and learned words with your Google account.",
    loginBtn: "Log in with Google",
  },
  admin: {
    title: "Admin · Students", sub: "Approve students so they can access the course.",
    name: "Name", email: "Email", status: "Status", joined: "Joined", role: "Role",
    approve: "Approve", revoke: "Revoke access", approved: "Approved", pending: "Pending",
    adminRole: "Admin", student: "Student", you: "You", noUsers: "No students yet.", loading: "Loading…",
    approvedToast: "Student approved ✓", revokedToast: "Access revoked",
  },
  hub: {
    title: "Practice", sub: "Sharpen your Finnish with games and exercises.",
    quizzes: "Lesson Quizzes", quizzesDesc: "Test yourself after each lesson with multiple-choice and fill-in questions.",
    match: "Match-Up Game", matchDesc: "Race the clock to match Finnish words with their meanings.",
    build: "Sentence Builder", buildDesc: "Tap the words into the correct Finnish word order.",
    listen: "Listening Drill", listenDesc: "Hear a Finnish sentence and choose what it means.",
    grammar: "Grammar Reference", grammarDesc: "Browse clear, concise grammar rules any time.",
    open: "Open",
  },
  culture: {
    title: "Finnish Life", sub: "Short notes on the culture behind the language.", funFact: "Did you know?",
  },
  grammarPage: {
    title: "Grammar Reference", sub: "Every key rule from the course, in one place.",
  },
  game: {
    start: "Start", score: "Score", time: "Time", restart: "Play again", check: "Check", clear: "Clear",
    correct: "Correct!", wrong: "Not quite — the answer was:", tapWords: "Tap the words in the right order:",
    playAudio: "Play sentence", whatHeard: "What does it mean?", next: "Next", done: "Done!",
    finalScore: "Your score", greatJob: "Great job!", matched: "Matched", timeUp: "Time's up!",
    reveal: "Show answer",
  },
  dict: {
    title: "Picture Dictionary", sub: "Everyday Finnish words with a picture. Tap any card to hear it.",
    scenesTitle: "One picture, many words", scenesSub: "See where everything is — tap a number to hear the word.",
    listen: "Listen",
  },
};

const bn = {
  nav: { courses: "কোর্স", vocab: "শব্দভাণ্ডার", practice: "অনুশীলন", progress: "অগ্রগতি", admin: "অ্যাডমিন", login: "লগ ইন", logout: "লগ আউট" },
  common: { back: "পিছনে", loading: "লোড হচ্ছে…", next: "পরবর্তী", prev: "পূর্ববর্তী" },
  home: {
    badge: "নতুনদের জন্য ফিনিশ · A1 ও A2",
    title: "ধাপে ধাপে ফিনিশ শিখুন",
    sub: "SuomiStep নতুন শিক্ষার্থীদের জন্য একটি নির্দেশিত ফিনিশ কোর্স — প্রথম শব্দ থেকে দৈনন্দিন কথোপকথন পর্যন্ত। নাম নথিভুক্ত করুন, শিক্ষকের অনুমোদন নিন এবং শেখা শুরু করুন।",
    ctaEnrol: "নথিভুক্ত করুন / লগ ইন",
    ctaLearnMore: "আপনি কী শিখবেন দেখুন",
    statLessons: "পাঠ",
    statWords: "শব্দ",
    statCases: "ব্যাকরণ কারক",
    coveredTitle: "এই কোর্সে আপনি যা শিখবেন",
    coveredSub: "সহজ থেকে কঠিন পর্যন্ত সাজানো একটি সম্পূর্ণ শিক্ষানবিশ পথ। কোর্সে যা যা আছে তার সবকিছু এখানে।",
    a1Title: "স্তর A1 · প্রাথমিক",
    a1Desc: "শূন্য থেকে শুরু: বর্ণমালা ও উচ্চারণ, অভিবাদন, সংখ্যা, দৈনন্দিন ব্যাকরণ এবং ক্যাফেতে অর্ডারের মতো সহজ কথোপকথন।",
    a2Title: "স্তর A2 · প্রারম্ভিক",
    a2Desc: "সত্যিকারের দক্ষতা গড়ুন: অতীত ও পুরাঘটিত কাল, প্রধান কারকগুলি, তুলনা এবং স্বাস্থ্য, বাসস্থান ও ভবিষ্যৎ পরিকল্পনার মতো ব্যবহারিক বিষয়।",
    howTitle: "কীভাবে কাজ করে",
    how1Title: "১. লগ ইন করুন",
    how1Body: "আপনার শিক্ষার্থী প্রোফাইল তৈরি করতে Google অ্যাকাউন্ট দিয়ে নিরাপদে সাইন ইন করুন।",
    how2Title: "২. অনুমোদন পান",
    how2Body: "আপনার শিক্ষক অনুরোধ পর্যালোচনা করে পাঠে প্রবেশাধিকার দেন।",
    how3Title: "৩. শেখা শুরু করুন",
    how3Body: "অনুমোদনের পর সমস্ত পাঠ, ফ্ল্যাশকার্ড, কুইজ ও অগ্রগতি খুলে যাবে।",
    enrolTitle: "শুরু করতে প্রস্তুত?",
    enrolBody: "প্রবেশাধিকারের অনুরোধ জানাতে লগ ইন করুন। শিক্ষক অনুমোদন দিলেই আপনি পড়া শুরু করতে পারবেন।",
    footer: "নতুন শিক্ষার্থীদের জন্য একটি মৌলিক ফিনিশ কোর্স।",
  },
  gate: {
    loginTitle: "চালিয়ে যেতে লগ ইন করুন",
    loginBody: "পাঠগুলি নথিভুক্ত শিক্ষার্থীদের জন্য। প্রবেশাধিকারের অনুরোধ জানাতে Google দিয়ে লগ ইন করুন।",
    loginBtn: "Google দিয়ে লগ ইন",
    pendingTitle: "আপনার প্রবেশাধিকার অনুমোদনের অপেক্ষায়",
    pendingBody: "নথিভুক্ত করার জন্য ধন্যবাদ! শুরু করার আগে শিক্ষককে আপনার অ্যাকাউন্ট অনুমোদন করতে হবে। শীঘ্রই আবার দেখুন।",
    pendingHint: "অনুমোদনের পর সমস্ত পাঠ, ফ্ল্যাশকার্ড ও কুইজে পূর্ণ প্রবেশাধিকার পাবেন।",
    backHome: "হোমে ফিরুন",
  },
  lesson: {
    back: "কোর্সে ফিরুন", complete: "সম্পূর্ণ চিহ্নিত করুন", completed: "সম্পন্ন", takeQuiz: "কুইজ দিন",
    keywords: "মূল শব্দ", testKnowledge: "নিজের জ্ঞান যাচাই করুন", listen: "শুনুন", loading: "লোড হচ্ছে…",
    saved: "দারুণ! পাঠ সম্পন্ন হিসেবে চিহ্নিত। 🎉", saveFail: "সংরক্ষণ করা যায়নি।",
  },
  flash: {
    title: "শব্দভাণ্ডার", sub: "কার্ড উল্টান এবং উচ্চারণ শুনুন।", all: "সব", tap: "উল্টাতে ট্যাপ করুন",
    translation: "অনুবাদ", know: "আমি জানি", learned: "শেখা হয়েছে", review: "পুনরালোচনা", mastered: "শেখা হয়েছে",
    savedLearned: "শেখা হিসেবে চিহ্নিত ✓", savedReview: "পুনরালোচনায় যোগ হয়েছে", learnedCount: "শেখা হয়েছে",
  },
  quiz: {
    hubTitle: "অনুশীলন", hubSub: "প্রতিটি পাঠের পর নিজের জ্ঞান যাচাই করুন।", qOf: "প্রশ্ন", check: "যাচাই করুন",
    next: "পরবর্তী", showResults: "ফলাফল দেখুন", correct: "সঠিক! খুব ভালো!", wrong: "ঠিক নয়। সঠিক উত্তর:",
    writeAnswer: "আপনার উত্তর লিখুন…", resultPerfect: "নিখুঁত! চমৎকার কাজ।", resultGood: "ভালো! অনুশীলন চালিয়ে যান।",
    resultBad: "ভালো চেষ্টা — পাঠটি পুনরায় দেখে আবার চেষ্টা করুন।", again: "আবার চেষ্টা করুন", backLesson: "পাঠে ফিরুন",
    saveLogin: "ফলাফল সংরক্ষণ করতে লগ ইন করুন।", loginBtn: "লগ ইন",
  },
  dash: {
    hi: "নমস্কার", welcome: "ফিনিশ শেখায় স্বাগতম।", streak: "দিনের ধারা", longest: "দীর্ঘতম",
    lessons: "পাঠ", accuracy: "কুইজ নির্ভুলতা", quizzesDone: "কুইজ সম্পন্ন", words: "শেখা শব্দ",
    levelProgress: "স্তরের অগ্রগতি", resumeFrom: "এখান থেকে চালিয়ে যান", allDone: "আপনি সব পাঠ সম্পন্ন করেছেন। অভিনন্দন!",
    calendar: "ধারা ক্যালেন্ডার", last5: "শেষ ৫ সপ্তাহ", noActivity: "কোনো কার্যকলাপ নেই", practiced: "অনুশীলিত",
    loginTitle: "অগ্রগতি দেখতে লগ ইন করুন", loginBody: "Google অ্যাকাউন্ট দিয়ে ধারা, স্কোর ও শেখা শব্দ ট্র্যাক করুন।",
    loginBtn: "Google দিয়ে লগ ইন",
  },
  admin: {
    title: "অ্যাডমিন · শিক্ষার্থী", sub: "শিক্ষার্থীদের অনুমোদন দিন যাতে তারা কোর্সে প্রবেশ করতে পারে।",
    name: "নাম", email: "ইমেইল", status: "অবস্থা", joined: "যোগদান", role: "ভূমিকা",
    approve: "অনুমোদন", revoke: "প্রবেশাধিকার বাতিল", approved: "অনুমোদিত", pending: "অপেক্ষমাণ",
    adminRole: "অ্যাডমিন", student: "শিক্ষার্থী", you: "আপনি", noUsers: "এখনো কোনো শিক্ষার্থী নেই।", loading: "লোড হচ্ছে…",
    approvedToast: "শিক্ষার্থী অনুমোদিত ✓", revokedToast: "প্রবেশাধিকার বাতিল হয়েছে",
  },
};

const hi = {
  nav: { courses: "कोर्स", vocab: "शब्दावली", practice: "अभ्यास", progress: "प्रगति", admin: "एडमिन", login: "लॉग इन", logout: "लॉग आउट" },
  common: { back: "वापस", loading: "लोड हो रहा है…", next: "अगला", prev: "पिछला" },
  home: {
    badge: "शुरुआती लोगों के लिए फ़िनिश · A1 और A2",
    title: "कदम-दर-कदम फ़िनिश सीखें",
    sub: "SuomiStep नए छात्रों के लिए एक मार्गदर्शित फ़िनिश कोर्स है — पहले शब्दों से लेकर रोज़मर्रा की बातचीत तक। नामांकन करें, शिक्षक से अनुमति लें और सीखना शुरू करें।",
    ctaEnrol: "नामांकन / लॉग इन",
    ctaLearnMore: "देखें आप क्या सीखेंगे",
    statLessons: "पाठ",
    statWords: "शब्द",
    statCases: "व्याकरण कारक",
    coveredTitle: "इस कोर्स में आप क्या सीखेंगे",
    coveredSub: "आसान से कठिन तक क्रमबद्ध एक संपूर्ण शुरुआती मार्ग। कोर्स में शामिल सब कुछ यहाँ है।",
    a1Title: "स्तर A1 · शुरुआती",
    a1Desc: "शून्य से शुरू: वर्णमाला और ध्वनियाँ, अभिवादन, संख्याएँ, रोज़मर्रा का व्याकरण और कैफ़े में ऑर्डर जैसी सरल बातचीत।",
    a2Title: "स्तर A2 · प्रारंभिक",
    a2Desc: "असली प्रवाह बनाएँ: भूत और पूर्ण काल, मुख्य कारक, तुलना और स्वास्थ्य, आवास व भविष्य की योजनाओं जैसे व्यावहारिक विषय।",
    howTitle: "यह कैसे काम करता है",
    how1Title: "1. लॉग इन करें",
    how1Body: "अपनी छात्र प्रोफ़ाइल बनाने के लिए Google खाते से सुरक्षित रूप से साइन इन करें।",
    how2Title: "2. अनुमति पाएँ",
    how2Body: "आपके शिक्षक अनुरोध की समीक्षा करते हैं और पाठों तक पहुँच देते हैं।",
    how3Title: "3. सीखना शुरू करें",
    how3Body: "अनुमति मिलते ही सभी पाठ, फ़्लैशकार्ड, क्विज़ और प्रगति खुल जाती है।",
    enrolTitle: "शुरू करने के लिए तैयार?",
    enrolBody: "पहुँच का अनुरोध करने के लिए लॉग इन करें। शिक्षक अनुमति देंगे और आप तुरंत पढ़ाई शुरू कर सकते हैं।",
    footer: "नए छात्रों के लिए एक मौलिक फ़िनिश कोर्स।",
  },
  gate: {
    loginTitle: "जारी रखने के लिए लॉग इन करें",
    loginBody: "पाठ नामांकित छात्रों के लिए हैं। पहुँच का अनुरोध करने के लिए Google से लॉग इन करें।",
    loginBtn: "Google से लॉग इन",
    pendingTitle: "आपकी पहुँच अनुमोदन की प्रतीक्षा में है",
    pendingBody: "नामांकन के लिए धन्यवाद! शुरू करने से पहले आपके शिक्षक को आपका खाता स्वीकृत करना होगा। कृपया जल्द ही फिर देखें।",
    pendingHint: "अनुमोदन के बाद आपको सभी पाठ, फ़्लैशकार्ड और क्विज़ तक पूर्ण पहुँच मिलेगी।",
    backHome: "होम पर वापस",
  },
  lesson: {
    back: "कोर्स पर वापस", complete: "पूर्ण चिह्नित करें", completed: "पूर्ण", takeQuiz: "क्विज़ दें",
    keywords: "मुख्य शब्द", testKnowledge: "अपना ज्ञान जाँचें", listen: "सुनें", loading: "लोड हो रहा है…",
    saved: "बढ़िया! पाठ पूर्ण चिह्नित। 🎉", saveFail: "सहेजा नहीं जा सका।",
  },
  flash: {
    title: "शब्दावली", sub: "कार्ड पलटें और उच्चारण सुनें।", all: "सभी", tap: "पलटने के लिए टैप करें",
    translation: "अनुवाद", know: "मुझे आता है", learned: "सीखा", review: "दोहराएँ", mastered: "सीखा",
    savedLearned: "सीखा हुआ चिह्नित ✓", savedReview: "दोहराने में जोड़ा", learnedCount: "सीखा",
  },
  quiz: {
    hubTitle: "अभ्यास", hubSub: "हर पाठ के बाद अपना ज्ञान जाँचें।", qOf: "प्रश्न", check: "जाँचें",
    next: "अगला", showResults: "परिणाम देखें", correct: "सही! शाबाश!", wrong: "बिलकुल नहीं। सही उत्तर:",
    writeAnswer: "अपना उत्तर लिखें…", resultPerfect: "बिल्कुल सही! शानदार।", resultGood: "अच्छा काम! अभ्यास जारी रखें।",
    resultBad: "अच्छी कोशिश — पाठ दोहराकर फिर प्रयास करें।", again: "फिर प्रयास करें", backLesson: "पाठ पर वापस",
    saveLogin: "अपने परिणाम सहेजने के लिए लॉग इन करें।", loginBtn: "लॉग इन",
  },
  dash: {
    hi: "नमस्ते", welcome: "फ़िनिश सीखने में आपका स्वागत है।", streak: "दिन की श्रृंखला", longest: "सबसे लंबी",
    lessons: "पाठ", accuracy: "क्विज़ सटीकता", quizzesDone: "क्विज़ पूर्ण", words: "सीखे शब्द",
    levelProgress: "स्तर प्रगति", resumeFrom: "यहाँ से जारी रखें", allDone: "आपने सभी पाठ पूरे कर लिए। बधाई!",
    calendar: "श्रृंखला कैलेंडर", last5: "पिछले 5 सप्ताह", noActivity: "कोई गतिविधि नहीं", practiced: "अभ्यास किया",
    loginTitle: "अपनी प्रगति देखने के लिए लॉग इन करें", loginBody: "Google खाते से अपनी श्रृंखला, स्कोर और सीखे शब्द ट्रैक करें।",
    loginBtn: "Google से लॉग इन",
  },
  admin: {
    title: "एडमिन · छात्र", sub: "छात्रों को स्वीकृत करें ताकि वे कोर्स तक पहुँच सकें।",
    name: "नाम", email: "ईमेल", status: "स्थिति", joined: "शामिल हुए", role: "भूमिका",
    approve: "स्वीकृत करें", revoke: "पहुँच हटाएँ", approved: "स्वीकृत", pending: "लंबित",
    adminRole: "एडमिन", student: "छात्र", you: "आप", noUsers: "अभी कोई छात्र नहीं।", loading: "लोड हो रहा है…",
    approvedToast: "छात्र स्वीकृत ✓", revokedToast: "पहुँच हटाई गई",
  },
};

const ne = {
  nav: { courses: "कोर्स", vocab: "शब्दावली", practice: "अभ्यास", progress: "प्रगति", admin: "एडमिन", login: "लग इन", logout: "लग आउट" },
  common: { back: "पछाडि", loading: "लोड हुँदै…", next: "अर्को", prev: "अघिल्लो" },
  home: {
    badge: "नयाँ विद्यार्थीका लागि फिनिस · A1 र A2",
    title: "पाइलैपिच्छे फिनिस सिक्नुहोस्",
    sub: "SuomiStep नयाँ विद्यार्थीहरूका लागि निर्देशित फिनिस कोर्स हो — पहिलो शब्ददेखि दैनिक कुराकानीसम्म। भर्ना गर्नुहोस्, शिक्षकबाट स्वीकृति लिनुहोस् र सिक्न सुरु गर्नुहोस्।",
    ctaEnrol: "भर्ना / लग इन",
    ctaLearnMore: "के सिक्नुहुन्छ हेर्नुहोस्",
    statLessons: "पाठ",
    statWords: "शब्द",
    statCases: "व्याकरण कारक",
    coveredTitle: "यस कोर्समा तपाईं के सिक्नुहुन्छ",
    coveredSub: "सजिलोदेखि कठिनसम्म क्रमबद्ध पूर्ण सुरुवाती बाटो। कोर्सले समेट्ने सबै कुरा यहाँ छ।",
    a1Title: "स्तर A1 · सुरुवाती",
    a1Desc: "शून्यबाट सुरु: वर्णमाला र ध्वनि, अभिवादन, संख्या, दैनिक व्याकरण र क्याफेमा अर्डर जस्ता सरल कुराकानी।",
    a2Title: "स्तर A2 · प्रारम्भिक",
    a2Desc: "वास्तविक दक्षता बनाउनुहोस्: भूत र पूर्ण काल, मुख्य कारकहरू, तुलना र स्वास्थ्य, आवास तथा भविष्यका योजना जस्ता व्यावहारिक विषय।",
    howTitle: "यो कसरी काम गर्छ",
    how1Title: "१. लग इन गर्नुहोस्",
    how1Body: "आफ्नो विद्यार्थी प्रोफाइल बनाउन Google खाताबाट सुरक्षित रूपमा साइन इन गर्नुहोस्।",
    how2Title: "२. स्वीकृति पाउनुहोस्",
    how2Body: "तपाईंको शिक्षकले अनुरोध समीक्षा गरी पाठमा पहुँच दिनुहुन्छ।",
    how3Title: "३. सिक्न सुरु गर्नुहोस्",
    how3Body: "स्वीकृति पाएपछि सबै पाठ, फ्ल्यासकार्ड, क्विज र प्रगति खुल्छ।",
    enrolTitle: "सुरु गर्न तयार?",
    enrolBody: "पहुँचको अनुरोध गर्न लग इन गर्नुहोस्। शिक्षकले स्वीकृति दिएपछि तपाईं तुरुन्तै पढ्न सुरु गर्न सक्नुहुन्छ।",
    footer: "नयाँ विद्यार्थीका लागि मौलिक फिनिस कोर्स।",
  },
  gate: {
    loginTitle: "जारी राख्न लग इन गर्नुहोस्",
    loginBody: "पाठहरू भर्ना भएका विद्यार्थीका लागि हुन्। पहुँच अनुरोध गर्न Google बाट लग इन गर्नुहोस्।",
    loginBtn: "Google बाट लग इन",
    pendingTitle: "तपाईंको पहुँच स्वीकृतिको प्रतीक्षामा छ",
    pendingBody: "भर्नाका लागि धन्यवाद! सुरु गर्नुअघि शिक्षकले तपाईंको खाता स्वीकृत गर्नुपर्छ। कृपया चाँडै फेरि हेर्नुहोस्।",
    pendingHint: "स्वीकृतिपछि तपाईंलाई सबै पाठ, फ्ल्यासकार्ड र क्विजमा पूर्ण पहुँच मिल्नेछ।",
    backHome: "गृहपृष्ठमा फर्कनुहोस्",
  },
  lesson: {
    back: "कोर्समा फर्कनुहोस्", complete: "पूरा भएको चिन्ह लगाउनुहोस्", completed: "सम्पन्न", takeQuiz: "क्विज दिनुहोस्",
    keywords: "मुख्य शब्द", testKnowledge: "आफ्नो ज्ञान जाँच्नुहोस्", listen: "सुन्नुहोस्", loading: "लोड हुँदै…",
    saved: "राम्रो! पाठ सम्पन्न भएको चिन्ह। 🎉", saveFail: "सुरक्षित गर्न सकिएन।",
  },
  flash: {
    title: "शब्दावली", sub: "कार्ड पल्टाउनुहोस् र उच्चारण सुन्नुहोस्।", all: "सबै", tap: "पल्टाउन ट्याप गर्नुहोस्",
    translation: "अनुवाद", know: "मलाई थाहा छ", learned: "सिकियो", review: "दोहोर्याउनुहोस्", mastered: "सिकियो",
    savedLearned: "सिकिएको चिन्ह ✓", savedReview: "दोहोर्याउनमा थपियो", learnedCount: "सिकियो",
  },
  quiz: {
    hubTitle: "अभ्यास", hubSub: "हरेक पाठपछि आफ्नो ज्ञान जाँच्नुहोस्।", qOf: "प्रश्न", check: "जाँच्नुहोस्",
    next: "अर्को", showResults: "नतिजा हेर्नुहोस्", correct: "सही! धेरै राम्रो!", wrong: "ठ्याक्कै होइन। सही उत्तर:",
    writeAnswer: "आफ्नो उत्तर लेख्नुहोस्…", resultPerfect: "उत्कृष्ट! सराहनीय काम।", resultGood: "राम्रो! अभ्यास जारी राख्नुहोस्।",
    resultBad: "राम्रो प्रयास — पाठ दोहोर्याएर फेरि प्रयास गर्नुहोस्।", again: "फेरि प्रयास", backLesson: "पाठमा फर्कनुहोस्",
    saveLogin: "आफ्नो नतिजा सुरक्षित गर्न लग इन गर्नुहोस्।", loginBtn: "लग इन",
  },
  dash: {
    hi: "नमस्ते", welcome: "फिनिस सिक्नमा स्वागत छ।", streak: "दिनको शृंखला", longest: "सबैभन्दा लामो",
    lessons: "पाठ", accuracy: "क्विज शुद्धता", quizzesDone: "क्विज सम्पन्न", words: "सिकिएका शब्द",
    levelProgress: "स्तर प्रगति", resumeFrom: "यहाँबाट जारी राख्नुहोस्", allDone: "तपाईंले सबै पाठ पूरा गर्नुभयो। बधाई!",
    calendar: "शृंखला पात्रो", last5: "पछिल्ला ५ हप्ता", noActivity: "कुनै गतिविधि छैन", practiced: "अभ्यास गरियो",
    loginTitle: "आफ्नो प्रगति हेर्न लग इन गर्नुहोस्", loginBody: "Google खाताबाट आफ्नो शृंखला, स्कोर र सिकिएका शब्द ट्र्याक गर्नुहोस्।",
    loginBtn: "Google बाट लग इन",
  },
  admin: {
    title: "एडमिन · विद्यार्थी", sub: "विद्यार्थीहरूलाई स्वीकृत गर्नुहोस् ताकि तिनीहरू कोर्समा पहुँच गर्न सकून्।",
    name: "नाम", email: "इमेल", status: "अवस्था", joined: "सामेल", role: "भूमिका",
    approve: "स्वीकृत", revoke: "पहुँच हटाउनुहोस्", approved: "स्वीकृत", pending: "प्रतीक्षारत",
    adminRole: "एडमिन", student: "विद्यार्थी", you: "तपाईं", noUsers: "अहिलेसम्म कुनै विद्यार्थी छैन।", loading: "लोड हुँदै…",
    approvedToast: "विद्यार्थी स्वीकृत ✓", revokedToast: "पहुँच हटाइयो",
  },
};

const DICTS = Object.fromEntries(
  Object.entries({ en, bn, hi, ne }).map(([k, d]) => [k, { ...d, ...EXTRA[k] }])
);

const LangContext = createContext(null);
export const useI18n = () => useContext(LangContext);

function lookup(dict, path) {
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), dict);
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("suomistep_lang") || "en");

  const setLang = useCallback((code) => {
    setLangState(code);
    localStorage.setItem("suomistep_lang", code);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (key) => {
      const val = lookup(DICTS[lang] || DICTS.en, key);
      if (val != null) return val;
      const fallback = lookup(DICTS.en, key);
      return fallback != null ? fallback : key;
    },
    [lang]
  );

  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}
